import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { MouseEvent } from 'react';
import {
  useCustomerSearch,
  useDuplicateDecision,
  useException,
  useExceptionSubmit,
  useOperatorResolutionSave,
  useProductSearch,
  useStageRetry,
} from '@/features/exceptions/hooks';
import { useOrderForfeit } from '@/features/orders/hooks';
import { useHasRole } from '@/auth/AuthProvider';
import {
  type TimelineActiveStep,
  type TimelineSubstepSelection,
  findTimelineStageIndex,
  TIMELINE_STAGES,
} from '@/components/common/StateMachineTimeline';
import type { CustomerAccountRole, CustomerOption, TimelineStageKey } from '@/api/cockpit';
import {
  manualLineDraftErrors, serializeManualLineDraft, describeManualLineSaveError,
  type ManualLineDraft,
} from './exception-detail/helpers/manual-line-drafts';
import {
  computeModifiedKeyFields,
  groupWarnings,
  isQueueItemReadOnly,
  mapActivityStageToTimelineStage,
  obstructiveFieldKeys,
  parseOcrCustomerContext,
  readShowPipelineSubstepsPreference,
  timelineActiveStepKey,
  writeShowPipelineSubstepsPreference,
} from './exception-detail/helpers';

export type RetryableStageKey = TimelineStageKey;

export interface TimelineContextMenuState {
  x: number;
  y: number;
  stageKey: RetryableStageKey;
  stageLabel: string;
  substepId?: string | null;
  substepLabel?: string | null;
}

function isRetryableStage(stageKey: TimelineStageKey): stageKey is RetryableStageKey {
  return stageKey !== 'approval' && stageKey !== 'resolved';
}

export function useExceptionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch, isFetching } = useException(id);
  const submit = useExceptionSubmit();
  const saveResolution = useOperatorResolutionSave();
  const stageRetry = useStageRetry();
  const forfeitOrder = useOrderForfeit();
  const duplicateDecision = useDuplicateDecision();
  const canDelete = useHasRole('SO-Admin');

  const initialFields = useMemo(() => {
    const acc: Record<string, string> = {};
    data?.fields.forEach((f) => {
      acc[f.key] = f.matchedValue ?? f.value;
    });
    return acc;
  }, [data]);

  const [fields, setFields] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState('');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [forfeitOpen, setForfeitOpen] = useState(false);
  const [duplicateDecisionOpen, setDuplicateDecisionOpen] = useState(false);
  const [forfeitReason, setForfeitReason] = useState('');
  const [skipForfeitAcknowledgement, setSkipForfeitAcknowledgement] = useState(false);
  const [approvalPromptDismissed, setApprovalPromptDismissed] = useState(false);
  const [showAllExecutionLogs, setShowAllExecutionLogs] = useState(false);
  const [selectedStageKey, setSelectedStageKey] = useState<TimelineStageKey | null>(null);
  const [selectedSubstep, setSelectedSubstep] = useState<TimelineSubstepSelection | null>(null);
  const [contextMenu, setContextMenu] = useState<TimelineContextMenuState | null>(null);
  const [showPipelineSubsteps, setShowPipelineSubsteps] = useState(() =>
    readShowPipelineSubstepsPreference(),
  );
  const [activeTimelineSteps, setActiveTimelineSteps] = useState<TimelineActiveStep[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Record<number, string>>({});
  const [selectedUnits, setSelectedUnits] = useState<Record<number, string>>({});
  const [selectedQuantities, setSelectedQuantities] = useState<Record<number, string>>({});
  const [manualLines, setManualLines] = useState<ManualLineDraft[]>([]);
  const [showManualLineErrors, setShowManualLineErrors] = useState(false);
  const [manualLineSaveError, setManualLineSaveError] = useState<string | null>(null);
  useEffect(() => { setManualLines([]); setShowManualLineErrors(false); setManualLineSaveError(null); }, [id]);
  const [excludedLines, setExcludedLines] = useState<
    Record<number, { category: string; reason: string }>
  >({});
  const [productPopoverLine, setProductPopoverLine] = useState<number | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchRole, setCustomerSearchRole] = useState<CustomerAccountRole>('ship_to');
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');
  const [relatedBillToSelection, setRelatedBillToSelection] = useState<CustomerOption | null>(null);
  // ADO #700: edit-time reason capture. fieldReasons holds committed
  // justifications, fieldReasonDrafts preserves in-flight popover text
  // across dismissals, reasonPopover tracks which field's popover is
  // currently open, and pendingDebouncedReasonKey is the most recent
  // free-text field still inside its 800 ms debounce window.
  const [fieldReasons, setFieldReasons] = useState<Record<string, string>>({});
  const [fieldReasonDrafts, setFieldReasonDrafts] = useState<Record<string, string>>({});
  const [reasonPopover, setReasonPopover] = useState<{
    reasonKey: string;
    label: string;
  } | null>(null);
  // ADO #700: queued "open the reason popover" intent. `delayMs` is short
  // (~200 ms) for dropdown/date/customer commits so the closing primitive
  // does not dispatch an outside-click against the newly-opened popover,
  // and longer (~800 ms) for free-text inputs so the operator has time to
  // finish typing. `revertCheck`, when present, cancels the open if the
  // value reverted to its previous committed value during the wait.
  const [pendingReasonOpen, setPendingReasonOpen] = useState<{
    reasonKey: string;
    label: string;
    delayMs: number;
    revertCheck: { pendingValue: string; previousValue: string } | null;
  } | null>(null);

  const extractedCustomerContext = useMemo(
    () => parseOcrCustomerContext(data?.ocrMetadata ?? null),
    [data?.ocrMetadata],
  );

  const merged: Record<string, string> = { ...initialFields, ...fields };
  // ADO #700: which key fields the operator has modified and which still
  // need a reason. Save is gated on `missingReasons.length === 0`.
  const modifiedKeyFields = useMemo(
    () =>
      computeModifiedKeyFields({
        data,
        mergedFields: merged,
        selectedProducts,
        selectedUnits,
        selectedQuantities,
        excludedLines,
      }),
    [data, merged, selectedProducts, selectedUnits, selectedQuantities, excludedLines],
  );
  const missingReasons = useMemo(
    () => modifiedKeyFields.filter((field) => !(fieldReasons[field.reasonKey] ?? '').trim()),
    [modifiedKeyFields, fieldReasons],
  );
  const canSubmit = feedback.trim().length >= 8 && !submit.isPending && !saveResolution.isPending;
  const queueItemReadOnly = data ? isQueueItemReadOnly(data.state, data.pipelineStageKey) : false;
  const hasResolutionChanges =
    !queueItemReadOnly &&
    (manualLines.length > 0 || Object.keys(fields).length > 0 ||
      Object.keys(excludedLines).length > 0 ||
      Object.entries(selectedProducts).some(([lineNumber, value]) => {
        const line = data?.lineItems.find(
          (candidate) => String(candidate.lineNumber) === lineNumber,
        );
        return (value ?? '') !== (line?.matchedProductCode ?? '');
      }) ||
      Object.entries(selectedUnits).some(([lineNumber, value]) => {
        const line = data?.lineItems.find(
          (candidate) => String(candidate.lineNumber) === lineNumber,
        );
        return (value ?? '') !== (line?.matchedUnitValue ?? '');
      }) ||
      Object.entries(selectedQuantities).some(([lineNumber, value]) => {
        const line = data?.lineItems.find(
          (candidate) => String(candidate.lineNumber) === lineNumber,
        );
        const current =
          line?.quantity === null || line?.quantity === undefined ? '' : String(line.quantity);
        return (value ?? '') !== current;
      }));
  const currentDisplayedStageKey = data?.pipelineStageKey ?? null;
  const currentStageIndex = data
    ? findTimelineStageIndex(data.state, currentDisplayedStageKey)
    : -1;
  const selectedStageDetail = selectedStageKey
    ? (data?.stageDetails[selectedStageKey] ?? null)
    : null;
  const productSearchResults = useProductSearch(
    id,
    debouncedProductSearch,
    productPopoverLine !== null,
  );
  const selectedLegalEntity =
    (fields.legal_entity ?? initialFields.legal_entity ?? '').toLowerCase() || null;
  const customerSearchResults = useCustomerSearch(
    id,
    debouncedCustomerSearch,
    selectedLegalEntity,
    customerSearchRole,
  );
  const warningGroups = data
    ? groupWarnings(data.warnings, data.warningDetails)
    : { informative: [], obstructive: [], fallback: [] };
  const highlightedErrorFields = useMemo(
    () => obstructiveFieldKeys(data?.warningDetails),
    [data?.warningDetails],
  );
  const hasBlockingProductReview = useMemo(
    () =>
      (data?.warningDetails ?? []).some(
        (warning) =>
          warning.category === 'obstructive' &&
          warning.fieldKeys.includes('line_items') &&
          (warning.substepId === 'product_matching' ||
            warning.message.toLowerCase().includes('d365 product')),
      ),
    [data?.warningDetails],
  );
  const isDraftRejectedReview =
    data?.state === 'Exception - Awaiting Operator' &&
    data.pipelineStageKey === 'matching' &&
    data.warningDetails?.some((warning) => warning.substepId === 'draft_rejected') === true;
  const isDuplicateReview =
    data?.state === 'Exception - Awaiting Operator' && data.pipelineStageKey === 'duplicate';
  const canApproveCurrentMatches =
    Boolean(id) &&
    data?.state === 'Exception - Awaiting Operator' &&
    !isDuplicateReview &&
    !isDraftRejectedReview &&
    !saveResolution.isPending;
  const isApprovalDraftWaiting =
    data !== undefined &&
    data.state === 'Awaiting Operator Approval' &&
    data.pipelineStageKey === 'approval';
  const showApprovalDraftPrompt = isApprovalDraftWaiting && !approvalPromptDismissed;

  useEffect(() => {
    if (!contextMenu) return;
    function closeMenu(): void {
      setContextMenu(null);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') closeMenu();
    }
    window.addEventListener('click', closeMenu);
    window.addEventListener('contextmenu', closeMenu);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('contextmenu', closeMenu);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [contextMenu]);

  useEffect(() => {
    setApprovalPromptDismissed(false);
  }, [id]);

  useEffect(() => {
    setRelatedBillToSelection(null);
  }, [id]);

  useEffect(() => {
    setSelectedProducts(
      Object.fromEntries(
        data?.lineItems.map((line) => [line.lineNumber, line.matchedProductCode ?? '']) ?? [],
      ),
    );
  }, [data]);

  useEffect(() => {
    setSelectedUnits(
      Object.fromEntries(
        data?.lineItems.map((line) => [line.lineNumber, line.matchedUnitValue ?? '']) ?? [],
      ),
    );
  }, [data]);

  useEffect(() => {
    setSelectedQuantities(
      Object.fromEntries(
        data?.lineItems.map((line) => [
          line.lineNumber,
          line.quantity === null ? '' : String(line.quantity),
        ]) ?? [],
      ),
    );
  }, [data]);

  useEffect(() => {
    setExcludedLines({});
  }, [data]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedProductSearch(productSearch.trim());
    }, 250);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [productSearch]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedCustomerSearch(customerSearch.trim());
    }, 250);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [customerSearch]);

  // ADO #700: open the reason popover after the configured delay. Free-
  // text fields use ~800 ms so the operator has time to finish typing;
  // dropdowns / dates / pickers use ~200 ms so the closing primitive
  // does not dispatch an outside-click against the newly-opened popover.
  useEffect(() => {
    if (!pendingReasonOpen) return;
    const { reasonKey, label, delayMs, revertCheck } = pendingReasonOpen;
    const timeout = window.setTimeout(() => {
      if (revertCheck && revertCheck.pendingValue === revertCheck.previousValue) {
        // Value reverted during the wait — skip the open.
        setPendingReasonOpen(null);
        return;
      }
      setReasonPopover({ reasonKey, label });
      setPendingReasonOpen(null);
    }, delayMs);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [pendingReasonOpen]);

  // ADO #700: when a modified key field is reverted (no longer in
  // `modifiedKeyFields`), clear its committed reason + draft so the badge
  // and Save gate stay accurate.
  useEffect(() => {
    const activeKeys = new Set(modifiedKeyFields.map((field) => field.reasonKey));
    setFieldReasons((current) => {
      const filtered = Object.fromEntries(
        Object.entries(current).filter(([key]) => activeKeys.has(key)),
      );
      return Object.keys(filtered).length === Object.keys(current).length ? current : filtered;
    });
    setFieldReasonDrafts((current) => {
      const filtered = Object.fromEntries(
        Object.entries(current).filter(([key]) => activeKeys.has(key)),
      );
      return Object.keys(filtered).length === Object.keys(current).length ? current : filtered;
    });
  }, [modifiedKeyFields]);

  useEffect(() => {
    if (!id) return;
    function onTransition(event: WindowEventMap['cockpit:transition']): void {
      if (event.detail.stagingRowId !== id) return;
      void refetch();
    }
    window.addEventListener('cockpit:transition', onTransition);
    return () => {
      window.removeEventListener('cockpit:transition', onTransition);
    };
  }, [id, refetch]);

  useEffect(() => {
    if (!id) return;
    function onStepActivity(event: WindowEventMap['cockpit:step-activity']): void {
      const detail = event.detail;
      if (detail.stagingRowId !== id) return;
      const stageKey = mapActivityStageToTimelineStage(detail.stage);
      if (!stageKey) return;
      const step: TimelineActiveStep = { stageKey, substepId: detail.substepId };
      const key = timelineActiveStepKey(step);
      setActiveTimelineSteps((current) => {
        if (detail.phase === 'started') {
          if (current.some((item) => timelineActiveStepKey(item) === key)) return current;
          return [...current, step];
        }
        return current.filter((item) => timelineActiveStepKey(item) !== key);
      });
    }
    window.addEventListener('cockpit:step-activity', onStepActivity);
    return () => {
      window.removeEventListener('cockpit:step-activity', onStepActivity);
    };
  }, [id]);

  useEffect(() => {
    if (!data) return;
    setActiveTimelineSteps((current) =>
      current.filter((step) => step.stageKey === data.pipelineStageKey),
    );
  }, [data?.pipelineStageKey]);

  useEffect(() => {
    writeShowPipelineSubstepsPreference(showPipelineSubsteps);
    if (!showPipelineSubsteps) {
      setSelectedSubstep(null);
    }
  }, [showPipelineSubsteps]);

  function onAction(action: 'resume' | 'close'): void {
    if (!data || !id) return;
    if (action === 'resume') {
      runSave({ fallbackReason: feedback.trim(), navigateOnSuccess: true });
      return;
    }
    submit.mutate(
      { id, fields: merged, feedback: feedback.trim(), action },
      {
        onSuccess: () => {
          navigate('/queue');
        },
      },
    );
  }

  function openStageContextMenu(
    event: MouseEvent,
    stageKey: TimelineStageKey,
    substep?: TimelineSubstepSelection | null,
  ): void {
    if (!isRetryableStage(stageKey)) return;
    const stage = TIMELINE_STAGES.find((candidate) => candidate.key === stageKey);
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      stageKey,
      stageLabel: stage?.label ?? stageKey,
      substepId: substep?.id ?? null,
      substepLabel: substep?.label ?? null,
    });
  }

  function runRetry(mode: 'single' | 'from_here'): void {
    if (!id || !contextMenu) return;
    const target = {
      stageKey: contextMenu.stageKey,
      substepId: contextMenu.substepId ?? null,
    };
    setContextMenu(null);
    stageRetry.mutate({
      id,
      mode,
      stageKey: target.stageKey,
      substepId: target.substepId,
    });
  }

  function runForfeit(): void {
    if (!id || forfeitReason.trim().length < 8) return;
    forfeitOrder.mutate(
      {
        id,
        reason: forfeitReason.trim(),
        skipCustomerAcknowledgement: skipForfeitAcknowledgement,
      },
      {
        onSuccess: () => {
          setForfeitOpen(false);
          setForfeitReason('');
          setSkipForfeitAcknowledgement(false);
          void refetch();
        },
      },
    );
  }

  function runDuplicateDecision(decision: 'duplicate' | 'not_duplicate'): void {
    if (!id) return;
    duplicateDecision.mutate(
      { id, decision },
      {
        onSuccess: () => {
          setDuplicateDecisionOpen(false);
          void refetch();
          if (decision === 'duplicate') navigate('/queue');
        },
      },
    );
  }

  function runSave(options: { fallbackReason?: string; navigateOnSuccess?: boolean } = {}): void {
    if (!id) return;
    const reasons = { ...fieldReasons };
    if (options.fallbackReason) {
      for (const field of modifiedKeyFields) {
        if (!reasons[field.reasonKey]?.trim()) {
          reasons[field.reasonKey] = options.fallbackReason.slice(0, 280);
        }
      }
    }
    if (modifiedKeyFields.some((field) => !reasons[field.reasonKey]?.trim())) return;
    setShowManualLineErrors(true);
    setManualLineSaveError(null);
    if (manualLines.some(line => Object.keys(manualLineDraftErrors(line)).length > 0)) {
      setManualLineSaveError('Review the required values for the new lines before saving.');
      document.getElementById('manual-lines-editor')?.scrollIntoView({ block: 'center' });
      return;
    }
    saveResolution.mutate(
      {
        id,
        addedLines: manualLines.map(serializeManualLineDraft),
        fields: merged,
        selectedProducts: Object.fromEntries(
          Object.entries(selectedProducts).map(([key, value]) => [String(key), value]),
        ),
        selectedUnits: Object.fromEntries(
          Object.entries(selectedUnits).map(([key, value]) => [String(key), value]),
        ),
        selectedQuantities: Object.fromEntries(
          Object.entries(selectedQuantities).map(([key, value]) => [String(key), value]),
        ),
        excludedLines: Object.fromEntries(
          Object.entries(excludedLines).map(([key, value]) => [String(key), value]),
        ),
        fieldReasons: reasons,
        ...(options.fallbackReason ? { feedback: options.fallbackReason } : {}),
      },
      {
        onSuccess: () => {
          setManualLines([]);
          setShowManualLineErrors(false);
          setManualLineSaveError(null);
          setFields({});
          setSelectedProducts({});
          setSelectedUnits({});
          setSelectedQuantities({});
          setFieldReasons({});
          setFieldReasonDrafts({});
          setExcludedLines({});
          setFeedbackOpen(false);
          if (options.navigateOnSuccess) {
            navigate('/queue');
          }
          void refetch();
        },
        onError: error => { setManualLineSaveError(describeManualLineSaveError(error)); },
      },
    );
  }

  // ADO #700: handlers for the reason popover. Both paths queue the open
  // through `pendingReasonOpen` so we can let the originating Select/Date/
  // free-text commit fully settle before the popover renders — opening
  // synchronously caused the dropdown's closing outside-click to dismiss
  // the popover immediately. Immediate-style commits use a short 200 ms
  // delay; free-text inputs use 800 ms for the typical typing pause.
  function requestReasonImmediate(reasonKey: string, label: string): void {
    setReasonPopover(null);
    setPendingReasonOpen({ reasonKey, label, delayMs: 200, revertCheck: null });
  }

  function requestReasonDebounced(args: {
    reasonKey: string;
    label: string;
    pendingValue: string;
    previousValue: string;
  }): void {
    setPendingReasonOpen({
      reasonKey: args.reasonKey,
      label: args.label,
      delayMs: 800,
      revertCheck: { pendingValue: args.pendingValue, previousValue: args.previousValue },
    });
  }

  function dismissReasonPopover(): void {
    setReasonPopover(null);
  }

  function setReasonDraft(reasonKey: string, draft: string): void {
    setFieldReasonDrafts((current) => ({ ...current, [reasonKey]: draft }));
  }

  function commitReason(reasonKey: string, text: string): void {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    setFieldReasons((current) => ({ ...current, [reasonKey]: trimmed }));
    setFieldReasonDrafts((current) => {
      const next = { ...current };
      delete next[reasonKey];
      return next;
    });
    setReasonPopover(null);
  }

  function commitLineExclusion(lineNumber: number, category: string, reason: string): void {
    const trimmed = reason.trim();
    const normalizedCategory = category.trim();
    if (trimmed.length < 8 || normalizedCategory.length === 0) return;
    setExcludedLines((current) => ({
      ...current,
      [lineNumber]: { category: normalizedCategory, reason: trimmed },
    }));
    const reasonKey = `line_exclusion:${lineNumber}`;
    setFieldReasons((current) => ({ ...current, [reasonKey]: trimmed }));
  }

  function removeLineExclusion(lineNumber: number): void {
    setExcludedLines((current) => {
      const next = { ...current };
      delete next[lineNumber];
      return next;
    });
    const reasonKey = `line_exclusion:${lineNumber}`;
    setFieldReasons((current) => {
      const next = { ...current };
      delete next[reasonKey];
      return next;
    });
  }

  return {
    manualLines, showManualLineErrors, manualLineSaveError,
    addManualLine: () => setManualLines(lines => [...lines, { id: crypto.randomUUID(), productCode: '', productDisplay: '', quantity: '', unitOfMeasure: '', unitPrice: '', currency: '', reason: '' }]),
    updateManualLine: (lineId: string, patch: Partial<ManualLineDraft>) => { setManualLines(lines => lines.map(line => line.id === lineId ? { ...line, ...patch } : line)); setManualLineSaveError(null); },
    removeManualLine: (lineId: string) => { setManualLines(lines => lines.filter(line => line.id !== lineId)); setProductPopoverLine(null); setManualLineSaveError(null); },
    // identity + navigation
    id,
    navigate,
    // query state
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    // permissions
    canDelete,
    // form state
    fields,
    setFields,
    feedback,
    setFeedback,
    feedbackOpen,
    setFeedbackOpen,
    forfeitOpen,
    setForfeitOpen,
    duplicateDecisionOpen,
    setDuplicateDecisionOpen,
    forfeitReason,
    setForfeitReason,
    skipForfeitAcknowledgement,
    setSkipForfeitAcknowledgement,
    approvalPromptDismissed,
    setApprovalPromptDismissed,
    showAllExecutionLogs,
    setShowAllExecutionLogs,
    // timeline state
    selectedStageKey,
    setSelectedStageKey,
    selectedSubstep,
    setSelectedSubstep,
    contextMenu,
    showPipelineSubsteps,
    setShowPipelineSubsteps,
    activeTimelineSteps,
    // line-item state
    selectedProducts,
    setSelectedProducts,
    selectedUnits,
    setSelectedUnits,
    selectedQuantities,
    setSelectedQuantities,
    excludedLines,
    commitLineExclusion,
    removeLineExclusion,
    // search state
    productPopoverLine,
    setProductPopoverLine,
    productSearch,
    setProductSearch,
    debouncedProductSearch,
    productSearchResults,
    customerSearch,
    setCustomerSearch,
    customerSearchRole,
    setCustomerSearchRole,
    debouncedCustomerSearch,
    customerSearchResults,
    relatedBillToSelection,
    setRelatedBillToSelection,
    // mutations (kept for pending flags)
    submit,
    saveResolution,
    stageRetry,
    forfeitOrder,
    duplicateDecision,
    // derived flags + values
    extractedCustomerContext,
    canSubmit,
    queueItemReadOnly,
    hasResolutionChanges,
    // ADO #700: reason capture state + handlers exposed to the UI.
    fieldReasons,
    fieldReasonDrafts,
    reasonPopover,
    modifiedKeyFields,
    missingReasons,
    requestReasonImmediate,
    requestReasonDebounced,
    dismissReasonPopover,
    setReasonDraft,
    commitReason,
    currentDisplayedStageKey,
    currentStageIndex,
    selectedStageDetail,
    warningGroups,
    highlightedErrorFields,
    hasBlockingProductReview,
    isDraftRejectedReview,
    isDuplicateReview,
    canApproveCurrentMatches,
    isApprovalDraftWaiting,
    showApprovalDraftPrompt,
    // handlers
    onAction,
    openStageContextMenu,
    runRetry,
    runForfeit,
    runDuplicateDecision,
    runSave,
  };
}
